// src/components/Dashboard/widgets/MilestonesWidget.tsx
// Widget 8 — Upcoming Milestones (next 5 across all jobs)
// Click milestone row → Contracts module at that contract, Milestones tab.
import { Calendar, ChevronRight, AlertCircle } from 'lucide-react'
import { getLiveMilestones } from '../../../utils/mockDataClaims'
import { formatCurrency } from '../../../utils/formatters'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function MilestonesWidget({ onNavigate, scopedJobIds }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const all = getLiveMilestones()
  const scoped = scopedJobIds
    ? all.filter(m => scopedJobIds.includes(m.jobId))
    : all

  const pending = scoped
    .filter(m => !['PAID'].includes(m.status))
    .sort((a, b) => {
      const da = (a as any).dueDate ? new Date((a as any).dueDate).getTime() : Infinity
      const db = (b as any).dueDate ? new Date((b as any).dueDate).getTime() : Infinity
      return da - db
    })
    .slice(0, 5)

  const today    = new Date()

  function daysTo(m: any): number | null {
    if (!m.dueDate) return null
    return Math.ceil((new Date(m.dueDate).getTime() - today.getTime()) / 86_400_000)
  }

  function urgencyStyle(days: number | null) {
    if (days === null) return { ring: 'bg-indigo-50',     icon: 'text-indigo-600',  badge: '',                          label: '' }
    if (days < 0)      return { ring: 'bg-red-100',       icon: 'text-red-600',     badge: 'bg-red-100 text-red-700',   label: `${Math.abs(days)}d overdue` }
    if (days <= 7)     return { ring: 'bg-amber-100',     icon: 'text-amber-600',   badge: 'bg-amber-100 text-amber-700', label: `${days}d` }
    return               { ring: 'bg-indigo-50',     icon: 'text-indigo-600',  badge: 'bg-gray-100 text-gray-600', label: `${days}d` }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('contracts')}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          <span className="font-semibold text-gray-900 text-sm">Upcoming milestones</span>
          <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium">
            {pending.length}
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* Milestone rows */}
      <div className="divide-y divide-gray-50 flex-1">
        {pending.map((m: any, idx: number) => {
          const days = daysTo(m)
          const u    = urgencyStyle(days)

          return (
            <div
              key={m.id ?? idx}
              className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
              onClick={() => navigate('contracts', { openId: m.jobId, openTab: 'milestones' })}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${u.ring}`}>
                {days !== null && (days < 0 || days <= 7)
                  ? <AlertCircle className={`w-3.5 h-3.5 ${u.icon}`} />
                  : <Calendar    className={`w-3.5 h-3.5 ${u.icon}`} />
                }
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">
                    {m.jobNumber ?? m.jobId ?? 'Job'}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    m.status === 'ELIGIBLE'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {m.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  {m.description ?? m.label ?? 'Milestone'}
                </p>
              </div>

              <div className="text-right flex-shrink-0 space-y-0.5">
                {u.label && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${u.badge}`}>
                    {u.label}
                  </span>
                )}
                {m.value > 0 && (
                  <p className="text-xs text-gray-400">{formatCurrency(m.value)}</p>
                )}
              </div>

              <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-gray-400 flex-shrink-0 transition-colors" />
            </div>
          )
        })}

        {pending.length === 0 && (
          <div className="px-5 py-8 text-center">
            <Calendar className="w-6 h-6 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No upcoming milestones</p>
          </div>
        )}
      </div>

      <div className="px-5 py-2.5 border-t">
        <button onClick={() => navigate('contracts')} className="text-xs text-blue-600 hover:underline">
          View all contracts →
        </button>
      </div>
    </div>
  )
}
